import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './SpecialProperty.css';
import { FaChevronLeft, FaChevronRight, FaMapMarkerAlt, FaStar, FaLayerGroup, FaRuler } from 'react-icons/fa';

export default function SpecialProperty() {
  const scrollRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [cardWidth, setCardWidth] = useState(410);

  // Calculate card width on mount and resize
  useEffect(() => {
    const calculateCardWidth = () => {
      if (window.innerWidth <= 480) {
        setCardWidth(315);
      } else if (window.innerWidth <= 768) {
        setCardWidth(340);
      } else if (window.innerWidth <= 1024) {
        setCardWidth(370);
      } else {
        setCardWidth(410);
      }
    };

    calculateCardWidth();
    window.addEventListener('resize', calculateCardWidth);
    return () => window.removeEventListener('resize', calculateCardWidth);
  }, []);

  // Special property data
  const properties = [
    {
      id: 1,
      slug: 'lutyens-heritage-bungalow',
      title: 'Heritage Bungalow',
      location: 'Lutyens Delhi',
      type: 'Heritage',
      builder: 'Government Estate',
      floors: 'Ground + 1st Floor',
      area: '10000 sq.ft',
      category: 'Special',
      image: 'https://images.pexels.com/photos/1396132/pexels-photo-1396132.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 2,
      slug: 'diplomatic-enclave-villa',
      title: 'Diplomatic Villa',
      location: 'Diplomatic Enclave',
      type: 'Villa',
      builder: 'Embassy Property',
      floors: 'Multi-Level',
      area: '15000 sq.ft',
      category: 'Special',
      image: 'https://images.pexels.com/photos/1370704/pexels-photo-1370704.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 3,
      slug: 'jorbagh-luxury-mansion',
      title: 'Luxury Mansion',
      location: 'Jor Bagh, New Delhi',
      type: 'Mansion',
      builder: 'Private Estate',
      floors: 'Ground + 2 Floors',
      area: '20000 sq.ft',
      category: 'Special',
      image: 'https://images.pexels.com/photos/1115804/pexels-photo-1115804.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 4,
      slug: 'rashtrapati-bhawan-estate',
      title: 'Presidential Estate',
      location: 'Near Rashtrapati Bhawan',
      type: 'Estate',
      builder: 'Historic Property',
      floors: 'Multiple Floors',
      area: '25000 sq.ft',
      category: 'Special',
      image: 'https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 5,
      slug: 'prithviraj-road-palace',
      title: 'Royal Palace',
      location: 'Prithviraj Road',
      type: 'Palace',
      builder: 'Royal Estate',
      floors: 'Grand Structure',
      area: '30000 sq.ft',
      category: 'Special',
      image: 'https://images.pexels.com/photos/2102587/pexels-photo-2102587.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 6,
      slug: 'shanti-path-ambassadorial',
      title: 'Ambassadorial Residence',
      location: 'Shanti Path, Delhi',
      type: 'Residence',
      builder: 'Diplomatic Property',
      floors: 'Ground + 2 Floors',
      area: '12000 sq.ft',
      category: 'Special',
      image: 'https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=800'
    }
  ];

  const scroll = (direction) => {
    const container = scrollRef.current;
    
    if (direction === 'left') {
      container.scrollBy({ left: -cardWidth, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: cardWidth, behavior: 'smooth' });
    }
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <>
      <section className="special-section">
        <div className="special-container">
          
          {/* Header */}
          <div className="section-header">
            <h2>Exclusive Special Properties</h2>
            <p>Explore our collection of rare and prestigious properties</p>
          </div>

          {/* Properties Slider */}
          <div className="slider-wrapper">
            <button 
              className="slider-btn slider-btn-left" 
              onClick={() => scroll('left')}
              aria-label="Scroll Left"
            >
              <FaChevronLeft />
            </button>

            <div 
              className="properties-slider" 
              ref={scrollRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {properties.map((property, index) => (
                <Link 
                  to={`/properties/special/${property.slug}`}
                  key={property.id}
                  style={{ textDecoration: 'none' }}
                >
                  <div 
                    className="property-card"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="property-image">
                      <img src={property.image} alt={property.title} />
                      <div className="property-badge">{property.category}</div>
                    </div>

                    <div className="property-content">
                      <h3>{property.title}</h3>
                      
                      <div className="property-details">
                        <div className="detail-item">
                          <FaMapMarkerAlt className="detail-icon" />
                          <span>{property.location}</span>
                        </div>
                        
                        <div className="detail-item">
                          <FaStar className="detail-icon" />
                          <span>{property.type}</span>
                        </div>
                        
                        <div className="detail-item">
                          <FaRuler className="detail-icon" />
                          <span>{property.area}</span>
                        </div>
                        
                        <div className="detail-item">
                          <FaLayerGroup className="detail-icon" />
                          <span>{property.floors}</span>
                        </div>
                      </div>

                      <div className="property-footer">
                        <div className="price-tag">Price on Request</div>
                        <button className="contact-btn">View Details</button>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <button 
              className="slider-btn slider-btn-right" 
              onClick={() => scroll('right')}
              aria-label="Scroll Right"
            >
              <FaChevronRight />
            </button>
          </div>

          {/* View All Button */}
          <div className="view-all-wrapper">
            <Link to="/contact">
              <button className="view-all-btn">Contact Us</button>
            </Link>
          </div>

        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="contact-cta-section">
        <div className="cta-content">
          <h2>Interested in Exclusive Properties?</h2>
          <p>Connect with our luxury property consultants today</p>
          <Link to="/contact" className="cta-link-btn">
            Get In Touch
          </Link>
        </div>
      </section>
    </>
  );
}
